import React, { type FC, memo, use, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@heroui/react";

import { productRoute } from "./productRoute";
import { reportLineText, reportToPlainText } from "./reportText";
import type { AgentReportData, ReportLine } from "./runner/types";
import { CompanyContext } from "@domain/context/CompanyContext";
import { useTranslation } from "@domain/intl/useTranslation";

interface Props {
    report: AgentReportData;
    /** Closes the modal; called after navigating to the drafts. */
    onClose?(): void;
}

interface SectionProps {
    heading: string;
    lines: string[];
    emptyText: string;
    testId: string;
}

const Section: FC<SectionProps> = ({ heading, lines, emptyText, testId }) => (
    <section data-testid={testId} aria-label={heading}>
        <h4 className="font-semibold">{heading}</h4>
        <ul className="mt-1 list-disc pl-5">
            {lines.length > 0 ? lines.map((text, i) => <li key={`${i}-${text}`}>{text}</li>) : <li>{emptyText}</li>}
        </ul>
    </section>
);

const AgentReportViewImpl: FC<Props> = ({ report, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { companyGQL } = use(CompanyContext);

    const text = useCallback((lines: ReportLine[]) => lines.map((l) => reportLineText(t, l)), [t]);
    const done = useMemo(() => text(report.done), [text, report.done]);
    const open = useMemo(() => text(report.open), [text, report.open]);
    const draftRoute = report.draftProduct ? productRoute(report.draftProduct) : undefined;

    const handleReview = useCallback(() => {
        if (!draftRoute) return;
        navigate(draftRoute);
        onClose?.();
    }, [draftRoute, navigate, onClose]);

    const handleDownload = useCallback(() => {
        const body = reportToPlainText(t, report, companyGQL?.name);
        const url = URL.createObjectURL(new Blob([body], { type: "text/plain;charset=utf-8" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `binale-agent-${report.year}.txt`;
        link.click();
        URL.revokeObjectURL(url);
    }, [t, report, companyGQL?.name]);

    return (
        <div className="flex flex-col gap-3">
            <Section
                testId="agent-report-done"
                heading={t("app.agent.report.sectionDone")}
                lines={done}
                emptyText={t("app.agent.report.nothingDone")}
            />
            <Section
                testId="agent-report-open"
                heading={t("app.agent.report.sectionOpen")}
                lines={open}
                emptyText={t("app.agent.report.nothingOpen")}
            />
            <div className="flex flex-wrap gap-2">
                {draftRoute && (
                    <Button variant="primary" className="rounded-full" onPress={handleReview}>
                        {t("app.agent.report.reviewDrafts")}
                    </Button>
                )}
                <Button variant="secondary" className="rounded-full" onPress={handleDownload}>
                    {t("app.agent.report.download")}
                </Button>
            </div>
        </div>
    );
};

export const AgentReportView = memo(AgentReportViewImpl);
